import { createAsyncThunk, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '../../error';
import { Crypto } from '../crypto';
import { ThunkExtra } from '../../utils';
import { CryptoType } from '../types';

import { CryptoState } from './crypto.state';

interface CryptoStateInterface {
  crypto: CryptoState;
}

export const getCertificates = createAsyncThunk<
  {
    id: string;
    name: string;
    user: string;
    company: string;
    activeFrom: string;
    activeTo: string;
    issuerName: string;
  }[],
  { cryptoType?: CryptoType },
  ThunkExtra<CryptoStateInterface>
>(
  'events/getCertificates',
  async ({ cryptoType }, { rejectWithValue, getState, extra: { inject } }) => {
    try {
      const { items, cryptoType: type } = getState().crypto;

      if (!!type && !!cryptoType && type === cryptoType) {
        return items;
      }

      const certificates =
        await inject(Crypto).getSerializedCertificates(cryptoType);

      return certificates;
    } catch (err) {
      const appError = new AppError('client', {
        code: 500,
        message: 'Ошибка при извлечении сертификатов',
      });

      /* istanbul ignore next */
      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);

export const cryptoReducers = (
  builder: ActionReducerMapBuilder<CryptoState>,
) => {
  builder.addCase(getCertificates.pending, (state) => {
    state.hasCertificates = state.status === 'complete' && !!state.items.length;
    state.status = 'loading';
    state.error = null;
  });
  builder.addCase(getCertificates.fulfilled, (state, { payload, meta }) => {
    state.status = 'complete';
    state.error = null;
    state.hasCertificates = !!payload.length;
    state.items = payload;
    state.cryptoType = meta.arg.cryptoType;
  });
  builder.addCase(getCertificates.rejected, (state, { payload }) => {
    state.status = 'failed';
    state.items = [];
    state.hasCertificates = false;
    /* istanbul ignore next */
    state.error = payload
      ? payload.title
      : 'Ошибка при извлечении сертификатов';
  });
};
